import type { LookupValue } from "@/types/task";

export type PriorityLevel = "low" | "medium" | "high" | "urgent";

export interface PriorityColorConfig {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

// Priority color mappings based on the Figma design specifications
export const PRIORITY_COLORS: Record<PriorityLevel, PriorityColorConfig> = {
  low: {
    backgroundColor: "rgba(108, 117, 125, 0.08)", // Gray with 8% opacity
    textColor: "#6C757D", // Gray-600 equivalent
    borderColor: "#6C757D",
  },
  medium: {
    backgroundColor: "rgba(55, 71, 149, 0.08)", // Marian Blue with 8% opacity (from Figma)
    textColor: "#374795", // Marian Blue (from Figma)
    borderColor: "#374795",
  },
  high: {
    backgroundColor: "rgba(231, 0, 11, 0.08)", // Red with 8% opacity (from Figma)
    textColor: "#e7000b", // Red (from Figma)
    borderColor: "#e7000b",
  },
  urgent: {
    backgroundColor: "rgba(220, 38, 38, 0.08)", // Darker red with 8% opacity
    textColor: "#DC2626", // Red-600
    borderColor: "#DC2626",
  },
};

/**
 * Get priority color configuration by priority label or value
 */
export const getPriorityColors = (
  priorityValue?: string
): PriorityColorConfig => {
  if (!priorityValue) {
    return PRIORITY_COLORS.medium; // Default to medium priority
  }

  const normalizedValue = priorityValue.toLowerCase() as PriorityLevel;

  return PRIORITY_COLORS[normalizedValue] || PRIORITY_COLORS.medium;
};

/**
 * Get priority color configuration from a LookupValue priority object
 */
export const getPriorityColorsFromLookup = (
  priority?: LookupValue
): PriorityColorConfig => {
  if (!priority) {
    return PRIORITY_COLORS.medium; // Default to medium priority
  }

  // First try to use the color from the lookup value if available
  if (priority.color) {
    return {
      backgroundColor: priority.color,
      textColor: "#FFFFFF",
      borderColor: priority.color,
    };
  }

  // Fallback to our predefined colors based on value or label
  const priorityIdentifier = (
    priority.value ||
    priority.label ||
    "medium"
  ).toLowerCase() as PriorityLevel;

  return PRIORITY_COLORS[priorityIdentifier] || PRIORITY_COLORS.medium;
};

/**
 * Get priority styles that match the exact Figma design
 */
export const getFigmaPriorityStyles = (
  priority?: LookupValue
): { backgroundColor: string; color: string } => {
  if (!priority) {
    return {
      backgroundColor: "rgba(55, 71, 149, 0.08)",
      color: "#374795",
    };
  }

  // If the priority has a custom color, use it with 8% opacity for background
  if (priority.color) {
    // Convert hex to rgba with 8% opacity
    const hex = priority.color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    return {
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.08)`,
      color: priority.color,
    };
  }

  const priorityIdentifier = (
    priority.value ||
    priority.label ||
    "medium"
  ).toLowerCase() as PriorityLevel;
  const colors = PRIORITY_COLORS[priorityIdentifier] || PRIORITY_COLORS.medium;

  return {
    backgroundColor: colors.backgroundColor,
    color: colors.textColor,
  };
};

/**
 * Get Tailwind CSS classes for priority styling (matching Figma design)
 */
export const getPriorityTailwindClasses = (priorityValue?: string): string => {
  if (!priorityValue) {
    return "bg-[rgba(55,71,149,0.08)] text-[#374795] border-[#374795]"; // Default to medium
  }

  const normalizedValue = priorityValue.toLowerCase() as PriorityLevel;

  switch (normalizedValue) {
    case "low":
      return "bg-[rgba(108,117,125,0.08)] text-[#6C757D] border-[#6C757D]";
    case "medium":
      return "bg-[rgba(55,71,149,0.08)] text-[#374795] border-[#374795]";
    case "high":
      return "bg-[rgba(231,0,11,0.08)] text-[#e7000b] border-[#e7000b]";
    case "urgent":
      return "bg-[rgba(220,38,38,0.08)] text-[#DC2626] border-[#DC2626]";
    default:
      return "bg-[rgba(55,71,149,0.08)] text-[#374795] border-[#374795]";
  }
};
